import { alpha, Box, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, FONT_SIZE, GAP_TONE, INK, LETTER_SPACING, NEUTRAL, STATUS } from "../../../../theme/tokens";
import type { BaseRequest } from "../../../../utils/models";
import { GAPS_DAY_LABEL_FORMAT } from "../gapsLabels";
import { type GapsRequestDayGroup, GapsRequestDayKind } from "../gapsRequests";

type Props = {
	group: Pick<GapsRequestDayGroup<BaseRequest>, "kind" | "dayKey" | "day">;
	/** How many of the day's requests are urgent claims — the band's own count. */
	urgentCount: number;
	openCount: number;
	neededCount: number;
};

/**
 * A day on Open Shifts: its name and how much is open on it, sticky while its requests scroll under it.
 *
 * A day holding an urgent claim — answered, starting within 48 hours — is drawn as the 48h band: the missing rows' and
 * the claims banner's critical wash with its status hairline, so the three red surfaces of the gaps view read as one
 * signal. "Today" is the CALENDAR day (P2-15), never a staffing day relabelled, and a day that is not today or
 * tomorrow is only ever named by its date.
 */
export const GapsRequestDayHeader: FC<Props> = ({ group, urgentCount, openCount, neededCount }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isBand = urgentCount > 0;
	// The artifact's day label: "Today · Thu 24 Sep", "Tomorrow · Fri 25 Sep", else the date alone.
	const dateLabel = group.day.toFormat(GAPS_DAY_LABEL_FORMAT);
	const dayName =
		group.kind === GapsRequestDayKind.Today ? t("gaps.requests.today") : group.kind === GapsRequestDayKind.Tomorrow ? t("gaps.requests.tomorrow") : null;

	return (
		<Box
			component="h2"
			data-testid={`gaps-requests-day-${group.dayKey}`}
			data-band={isBand}
			sx={{
				position: "sticky",
				top: 0,
				zIndex: 1,
				m: 0,
				display: "flex",
				alignItems: "baseline",
				gap: 0.75,
				px: 2,
				py: 1,
				bgcolor: isBand ? GAP_TONE.CRITICAL_BG : NEUTRAL[50],
				borderBottom: `1px solid ${isBand ? alpha(theme.palette.error.main, ALPHA.STATUS_BORDER) : NEUTRAL[150]}`,
				fontSize: FONT_SIZE.CAPTION,
				fontWeight: 400,
				color: INK.SECONDARY,
			}}
		>
			<Box component="span" sx={{ fontWeight: 700, color: isBand ? STATUS.ERROR.main : INK.PRIMARY }}>
				{dayName ?? dateLabel}
			</Box>
			{dayName !== null && <Box component="span">{`· ${dateLabel}`}</Box>}
			<Typography
				component="span"
				data-testid={`gaps-requests-day-${group.dayKey}-count`}
				sx={{
					ml: "auto",
					fontSize: FONT_SIZE.LABEL_XS,
					letterSpacing: LETTER_SPACING.LABEL,
					textTransform: "uppercase",
					fontWeight: 600,
					color: isBand ? STATUS.ERROR.main : INK.MUTED,
					fontVariantNumeric: "tabular-nums",
					whiteSpace: "nowrap",
				}}
			>
				{isBand
					? t("gaps.requests.dayUrgent", { count: urgentCount })
					: `${t("gaps.requests.dayOpen", { count: openCount })} · ${t("gaps.requests.dayNeeded", { count: neededCount })}`}
			</Typography>
		</Box>
	);
};
